<template>
    <main class="mb-5">
        <div id="up" class="w-full">
            <div class="w-full mb-2 text-center">
                <h1 class="text-3xl">
                    {{ isQuote ? "Cotización" : "Venta" }}
                    <template v-if="hasMerchant">
                        en
                        <span class="text-theme-primary capitalize">
                            {{
                                selectedMerchant.merchantType
                                    | merchantInfo("type")
                            }}
                            {{ selectedMerchant.name }}
                        </span>
                    </template>
                    {{
                        sourcePriceMerchant
                            ? "con precios de tienda"
                            : `con precios del ${$siteType} (catálogo)`
                    }}
                </h1>
            </div>
        </div>

        <div v-if="isOnboarding" class="w-full my-3 text-justify">
            <span>
                Finalmente, registra una venta. Ve al buscador de productos de
                la parte inferior y escribe de nuevo
                <strong> cuaderno </strong> o el nombre del producto que
                ingresaste, es importante hacer referencia a este producto, ya
                que el buscador solo buscará en tu inventario. Si NO recuerdas
                el producto que ingresaste, registralo de nuevo en el inventario
                dando click
                <router-link to="/onboarding/marketplace/inventories">
                    aqui </router-link
                >.
                <br />
                <br />
                Cuando tengas los productos seleccionados da click en el botón
                "Crear Venta" el sistema registrará la venta, decrementará las
                unidades de tu inventario e incrementará el dinero disponible en
                la caja.
            </span>
        </div>

        <div
            class="w-full flex flex-wrap-col-reverse md:flex-row justify-between items-center my-2"
        >
            <component
                :is="
                    sourcePriceMerchant
                        ? 'inventories-barcode-scanner'
                        : 'products-barcode-scanner'
                "
                v-model="enabledScanner"
                :enabled="!isEnabledServiceSearch"
                @input="selectProduct"
            />
            <div class="flex items-center justify-between">
                <tooltip-button
                    icon="ℹ️"
                    message="¿ Necesitas ayuda para registrar ventas ?"
                    @click="enableHelp = true"
                />
                <tooltip-button
                    v-if="!isOnboarding"
                    icon="⚙"
                    message="Opciones"
                    @click="enableConfig = !enableConfig"
                />
            </div>
        </div>
        <process-guide
            v-model="enableHelp"
            title="¿Cómo registrar una venta?"
            :intro="helpIntro"
            :steps="helpSteps"
        />

        <div v-show="enableConfig">
            <wiki-learn-more-link topic="opciones-de-venta" class="mt-2" />
            <div
                class="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-3 mt-3"
            >
                <div>
                    <custom-checkbox v-model="isQuote" warn>
                        <span> Es una cotización </span>
                    </custom-checkbox>
                </div>
                <div v-if="$ability.hasPermission('customer:List')">
                    <custom-checkbox v-model="orderNeedCustomer" success>
                        <span>
                            {{
                                customerName && orderNeedCustomer
                                    ? customerName
                                    : "Asociar venta a cliente"
                            }}
                        </span>
                    </custom-checkbox>
                </div>
                <div v-if="$ability.hasPermission('user:ListOwn')">
                    <custom-checkbox v-model="orderNeedSeller" success>
                        <span>
                            {{
                                sellerName && orderNeedSeller
                                    ? sellerName
                                    : "Asociar venta a asesor"
                            }}
                        </span>
                    </custom-checkbox>
                </div>
                <div>
                    <custom-checkbox v-model="orderNeedObservation" warn>
                        <span v-if="orderNeedObservation && observation">
                            {{ observation | cut(20) }}
                        </span>
                        <span v-else> Comentarios adicionales </span>
                    </custom-checkbox>
                </div>
                <div v-if="!isQuote">
                    <custom-checkbox v-model="orderNeedPartialPaid" warn>
                        <span v-if="orderNeedPartialPaid">
                            Abono de {{ valuePaid | money }}
                        </span>
                        <span v-else> Abono </span>
                    </custom-checkbox>
                </div>
                <div>
                    <custom-checkbox v-model="orderNeedDeliveryAt" warn>
                        <span v-if="orderNeedDeliveryAt && deliveryAt">
                            Fecha de entrega {{ deliveryAt }}
                        </span>
                        <span v-else> Especificar fecha de entrega </span>
                    </custom-checkbox>
                </div>

                <div
                    v-if="
                        !sourcePriceMerchant &&
                        selectedMerchant.merchantId &&
                        $ability.hasPermission('order:CreateInMerchant')
                    "
                >
                    <custom-checkbox v-model="attachToSelectedMerchant" success>
                        <span>
                            Asociar a "{{ selectedMerchant.name }}" (descuenta
                            su inventario y suma a su caja)
                        </span>
                    </custom-checkbox>
                </div>

                <div v-if="!isQuote && hasMerchant">
                    <custom-checkbox
                        v-model="notVerifyAndDecreaseInventory"
                        warn
                    >
                        <span> No verificar ni decrementar inventario </span>
                    </custom-checkbox>
                </div>

                <div v-if="$ability.hasPermission('customer:List')">
                    <custom-checkbox v-model="orderNeedHomeDelivery" warn>
                        <span v-if="orderNeedHomeDelivery && deliveryLocation">
                            Domicilio por {{ deliveryPrice | money }}
                        </span>
                        <span v-else> Pedido a domicilio </span>
                    </custom-checkbox>
                </div>
            </div>
        </div>

        <div class="flex md:flex-col flex-col-reverse">
            <div class="w-full flex flex-wrap md:flex-row flex-col-reverse">
                <div
                    class="w-full md:w-8/12 md:p-0 flex flex-col justify-center"
                >
                    <h2 class="mt-1 mb-0 h5">
                        {{
                            isEnabledServiceSearch
                                ? "Selecciona servicios"
                                : "Selecciona productos"
                        }}
                    </h2>
                    <div class="w-full flex items-end mt-2">
                        <service-selector
                            v-if="isEnabledServiceSearch"
                            :base="selectedMerchant.services"
                            @input="selectProductAnonymous"
                        />
                        <component
                            :is="
                                sourcePriceMerchant
                                    ? 'inventories-search'
                                    : 'products-search'
                            "
                            v-else
                            v-model="lastProductSelected"
                            @focus="disableScanner"
                            @blur="enableScanner"
                            @input="selectProduct"
                        />
                        <custom-tooltip
                            v-if="
                                selectedMerchant.merchantId &&
                                selectedMerchant.services.length
                            "
                            border
                        >
                            <app-button icon @click="toggleIsEnabledServices">
                                {{ isEnabledServiceSearch ? "🛍" : "👩‍🏭" }}
                            </app-button>
                            <template #tooltip>
                                <div class="content-tooltip">
                                    <span>
                                        {{
                                            isEnabledServiceSearch
                                                ? "Cambia a buscador de productos"
                                                : "Cambia a buscador de servicios"
                                        }}
                                    </span>
                                </div>
                            </template>
                        </custom-tooltip>
                        <product-anonymous-button
                            class="ml-1"
                            @input="selectProductAnonymous"
                            @open="disableScanner"
                            @close="enableScanner"
                        />
                    </div>
                </div>
                <div class="w-full md:w-4/12 md:pr-0 md:pl-3">
                    <payment-methods-checker
                        v-model="paymentMethod"
                        :allowed-methods="portalPaymentMethods"
                    >
                        <template #title>
                            <span class="font-bold">Método de pago</span>
                        </template>
                    </payment-methods-checker>
                </div>
            </div>
        </div>

        <div class="z-0 mt-3 min-h-24">
            <h4 v-show="references.length">Productos seleccionados</h4>
            <reference-table-item
                v-for="(reference, index) in references"
                :key="index"
                :reference="reference"
                enabled-discount
                enabled-sale-price
                allow-above-catalog
                enabled
                class="md:w-11/12 w-full mx-auto"
                @update="changeQuantityReference"
                @remove="removeReference"
                @focus="disableScanner"
            />
            <h4 v-show="anonymousReferences.length" class="mt-3">
                Servicios / Productos anónimos
            </h4>
            <reference-table-item
                v-for="(reference, index) in anonymousReferences"
                :key="'B' + index"
                :reference="reference"
                class="md:w-11/12 w-full mx-auto"
                enabled
                @update="changeQuantityAnonymousReference"
                @remove="removeAnonymousReference"
                @focus="disableScanner"
            />
        </div>

        <div
            v-if="total > 0"
            class="w-full flex justify-center flex-wrap z-0 mt-4 mb-5"
        >
            <div class="w-full flex lg:flex-row flex-col justify-end">
                <div class="flex flex-col">
                    <h5 class="text-xl">Subtotal : {{ subtotal | money }}</h5>
                    <h5
                        v-if="orderNeedHomeDelivery && deliveryLocation"
                        class="text-xl mt-1"
                    >
                        Envío : {{ deliveryPrice | money }}
                    </h5>
                    <h4 class="text-4xl text-theme-primary mt-1 font-bold">
                        Total : {{ total | money }}
                    </h4>
                </div>
            </div>
        </div>

        <div class="w-full flex justify-center">
            <app-button
                id="create-order"
                class="mt-5 mb-7 w-3/4 mx-auto"
                border
                gradient
                :disabled="isCreateDisabled"
                @click="createOrderComponent"
            >
                <span class="text-2xl"> Crear {{ bottonActionLabel }} </span>
            </app-button>
        </div>

        <div
            v-if="isOnboarding"
            class="flex justify-center my-3 link-secondary"
        >
            <router-link to="/app"> Omitir </router-link>
        </div>

        <div id="bottom"></div>

        <product-reference-modal
            v-model="isOpenReferencesModal"
            :product-name="productTmp.name"
            :references="productTmp.references"
            :reference-selected="referenceSelected"
            text-size="text-2xl"
            :margin-top="false"
            @close="enableScanner"
            @select="selectReference"
        />

        <app-dialog
            v-model="isOpenSellersModal"
            size="lg"
            @close="closeSellersModal"
        >
            <template #header>
                <h3 class="text-xl">Selecciona vendedor 👩‍💼</h3>
            </template>
            <user-by-merchant-selector-2
                v-if="isOpenSellersModal"
                :merchant="selectedMerchant"
                @change="selectSeller"
            />
        </app-dialog>

        <app-dialog
            v-model="isOpenHomeDeliveryModal"
            size="lg"
            @close="closeHomeDeliveryModal"
        >
            <template #header>
                <div class="flex justify-center items-center gap-2">
                    <span class="text-xl"> Pedido a domicilio </span>
                    <app-button
                        icon
                        circle
                        :disabled="!deliveryLocation"
                        @click="isOpenHomeDeliveryModal = false"
                    >
                        ✔️
                    </app-button>
                </div>
            </template>
            <div v-if="customerId" class="flex flex-col gap-4">
                <span>Dirección de entrega de {{ customerName }}</span>
                <delivery-location-selector
                    :value="deliveryLocation"
                    :customer-id="customerId"
                    @input="onDeliveryLocationChange"
                />
                <input-money-component
                    v-model="deliveryPrice"
                    border
                    enabled
                    label="Costo de envío"
                    icon="🚚"
                    @input="onDeliveryPriceInput"
                />
            </div>
        </app-dialog>

        <app-dialog
            v-model="isOpenCustomersModal"
            size="lg"
            @close="closeCustomersModal"
        >
            <template #header>
                <h3 class="text-3xl">Buscador de clientes 👩‍🎤</h3>
            </template>

            <search-user
                is-customer
                @change="selectCustomer"
                @focus="disableScanner"
                @blur="enableScanner"
            />
        </app-dialog>

        <app-dialog
            v-model="isOpenValuePaidModal"
            size="sm"
            @close="enableScanner"
        >
            <template #header>
                <div class="flex justify-center items-center gap-2">
                    <span class="text-xl"> Abono </span>
                    <app-button icon circle @click="selectValuePaid">
                        ✔️
                    </app-button>
                </div>
            </template>
            <input-money-component
                v-model="valuePaid"
                border
                label="Abono"
                icon="💲"
            />
        </app-dialog>

        <app-dialog v-model="isOpenObservationModal" @close="enableScanner">
            <template #header>
                <div class="flex justify-center items-center gap-2">
                    <span class="text-xl"> Comentarios adicionales </span>
                    <app-button icon circle @click="selectObservation">
                        ✔️
                    </app-button>
                </div>
            </template>
            <textarea
                v-model="observation"
                class="app-textarea text-theme-base"
                placeholder="Comentarios"
                rows="5"
                @focus="disableScanner"
                @blur="enableScanner"
            ></textarea>
        </app-dialog>

        <app-dialog
            v-model="isOpenDeliveryAtModal"
            @close="closeDeliveryAtModal"
        >
            <template #header>
                <div class="flex justify-center items-center gap-2">
                    <span class="text-lg"> Fecha de entrega </span>
                    <app-button icon circle @click="selectDeliveryAt">
                        ✔️
                    </app-button>
                </div>
            </template>
            <date-picker
                v-model="deliveryAt"
                :min="$data.$minDeliveryDate"
                always-open
                locale="es"
                size="lg"
            />
        </app-dialog>

        <app-dialog
            v-model="isOpenMoneyBackModal"
            persistent
            @close="onEnterMoneyIngress"
        >
            <template #header>
                <div class="flex flex-col justify-center">
                    <span class="text-xl">
                        Valor de venta {{ orderResult.total | currency }}
                    </span>
                    <span
                        v-if="orderResult.valuePaid !== orderResult.total"
                        class="text-md mt-2"
                    >
                        Valor de abono {{ orderResult.valuePaid | currency }}
                    </span>
                </div>
            </template>
            <div
                class="input-number-simple flex flex-col justify-center items-center"
            >
                <div class="">
                    <strong class="text-lg">
                        Ingresa el monto con que
                        {{ isPartialPayment ? "abona" : "paga" }}
                    </strong>
                </div>
                <div class="flex items-center mt-2">
                    <div>
                        <input-money-component-2
                            v-model="moneyIngress"
                            autofocus
                            @enter="isOpenMoneyBackModal = false"
                        />
                    </div>
                    <app-button
                        :disabled="!isValidMoneyIngress"
                        class="ml-1"
                        icon
                        circle
                        @click="isOpenMoneyBackModal = false"
                    >
                        ✔️
                    </app-button>
                </div>

                <div v-if="!isValidMoneyIngress" class="mt-3">
                    <span class="text-theme-warning">
                        El dinero ingresado debe ser mayor a el valor de la
                        venta.
                    </span>
                </div>
            </div>
        </app-dialog>

        <app-dialog v-model="isOpenResultModal" size="lg" @close="closeResult">
            <template #header>
                <div class="flex flex-col items-center">
                    <span class="text-3xl text-theme-primary font-bold">
                        ✅ {{ bottonActionLabel }} #{{ orderResult.id }}
                    </span>
                    <span class="text-lg text-theme-muted mt-1">
                        Registrada exitosamente
                    </span>
                </div>
            </template>
            <div>
                <div
                    v-if="moneyIngress > 0"
                    class="bg-theme-canvas rounded-lg p-4 mb-4"
                >
                    <div class="flex flex-col justify-center items-center">
                        <span class="text-xl font-semibold mb-2">
                            💵 Información de pago
                        </span>
                        <div class="w-full flex flex-col gap-2">
                            <div class="flex justify-between items-center">
                                <span class="text-base">Total venta:</span>
                                <span class="font-bold text-lg">
                                    {{ orderResult.total | currency }}
                                </span>
                            </div>
                            <div
                                v-if="
                                    orderResult.valuePaid !== orderResult.total
                                "
                                class="flex justify-between items-center"
                            >
                                <span class="text-base">Abono:</span>
                                <span class="font-bold text-lg">
                                    {{ orderResult.valuePaid | currency }}
                                </span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-base">Dinero recibido:</span>
                                <span
                                    class="font-bold text-lg text-theme-primary"
                                >
                                    {{ moneyIngress | currency }}
                                </span>
                            </div>
                            <div
                                v-if="moneyBack"
                                class="flex justify-between items-center border-t pt-2"
                            >
                                <span class="text-base font-semibold">
                                    Vuelto:
                                </span>
                                <span
                                    class="font-bold text-2xl text-theme-success"
                                >
                                    {{ moneyBack | currency }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-theme-canvas rounded-lg p-4 mb-4">
                    <div class="flex flex-col gap-2">
                        <span class="text-xl font-semibold mb-2">
                            📋 Resumen de la orden
                        </span>
                        <div
                            v-if="
                                orderResult.customer &&
                                orderResult.customer.firstName
                            "
                            class="flex items-center gap-2"
                        >
                            <span class="font-semibold">Cliente:</span>
                            <span>{{ orderResult.customer.firstName }}</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="font-semibold">Método de pago:</span>
                            <span class="capitalize">
                                {{
                                    $data.$paymentMethodsCodes[
                                        orderResult.paymentMethod
                                    ]
                                }}
                            </span>
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="font-semibold">Estado de pago:</span>
                            <span class="px-2 py-1 rounded text-sm">
                                {{
                                    $data.$paymentStatusesCodes[
                                        orderResult.paymentStatus
                                    ]
                                }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="mt-4">
                    <order-ticket-printer
                        show
                        show-print
                        :order="orderResult"
                    />
                </div>

                <div class="mt-4 flex flex-wrap justify-center gap-2">
                    <app-button
                        v-if="!isOnboarding"
                        @click="isOpenResultModal = false"
                    >
                        💰 Registrar nueva venta
                    </app-button>
                    <app-button
                        v-if="!isOnboarding"
                        :to="
                            sourcePriceMerchant
                                ? `/app/orders/${selectedMerchant.merchantType}/${selectedMerchant.merchantId}/detail/${orderResult.id}`
                                : `/app/orders/detail/${orderResult.id}`
                        "
                    >
                        🐱‍💻 Ver detalles completos
                    </app-button>
                </div>
            </div>
        </app-dialog>
    </main>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import { paymentMethodsCodes } from "@/common/fields/orders-payment-methods";
import { paymentStatusesCodes } from "@/common/fields/orders-payment-status-codes";
import { ordersStatusesCodes } from "@/common/fields/orders-status-codes";
import {
    resolveLineSalePrice,
    validateLineSalePrice
} from "@/common/lib/order-line-price";

import InventoriesBarcodeScanner from "@/components/inventories/InventoriesBarcodeScanner.vue";
import InventoriesSearch from "@/components/inventories/InventoriesSearch.vue";
import DeliveryLocationSelector from "@/components/orders/DeliveryLocationSelector.vue";
import OrderTicketPrinter from "@/components/orders/OrderTicketPrinter.vue";
import PaymentMethodsChecker from "@/components/orders/PaymentMethodsChecker.vue";
import ProductAnonymousButton from "@/components/products/ProductAnonymousButton.vue";
import ProductsBarcodeScanner from "@/components/products/ProductsBarcodeScanner.vue";
import ProductsSearch from "@/components/products/ProductsSearch.vue";
import ReferenceTableItem from "@/components/references/ReferenceTableItem.vue";
import SearchUser from "@/components/users/SearchUser.vue";
import UserByMerchantSelector2 from "@/components/users/UserByMerchantSelector2.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import DatePicker from "@/components/utils/DatePicker.vue";
import InputMoneyComponent2 from "@/components/utils/InputMoneyComponent2.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";
import ProcessGuide from "@/components/utils/ProcessGuide.vue";
import ProductReferenceModal from "@/components/utils/ProductReferenceModal.vue";
import ServiceSelector from "@/components/utils/ServiceSelector.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";
import WikiLearnMoreLink from "@/components/utils/WikiLearnMoreLink.vue";

export default {
    name: "OrderNew",
    components: {
        AppDialog,
        DatePicker,
        DeliveryLocationSelector,
        SearchUser,
        OrderTicketPrinter,
        TooltipButton,
        ProductsSearch,
        InventoriesSearch,
        InputMoneyComponent,
        ReferenceTableItem,
        ProductsBarcodeScanner,
        ProductAnonymousButton,
        InventoriesBarcodeScanner,
        PaymentMethodsChecker,
        UserByMerchantSelector2,
        InputMoneyComponent2,
        ProductReferenceModal,
        ServiceSelector,
        ProcessGuide,
        WikiLearnMoreLink
    },
    data: () => ({
        total: 0,
        subtotal: 0,
        valuePaid: 0,
        observation: "",
        references: [],
        sellerName: "",
        customerName: "",
        moneyIngress: 0,
        moneyBack: 0,
        sellerId: null,
        customerId: null,
        enableHelp: false,
        enableConfig: false,
        isOnboarding: false,
        $paymentMethodsCodes: paymentMethodsCodes,
        $paymentStatusesCodes: paymentStatusesCodes,
        $ordersStatusesCodes: ordersStatusesCodes,
        enabledScanner: false,
        isQuote: false,
        attachToSelectedMerchant: false,
        orderNeedSeller: false,
        orderNeedCustomer: false,
        orderNeedObservation: false,
        orderNeedDeliveryAt: false,
        orderNeedHomeDelivery: false,
        isOpenHomeDeliveryModal: false,
        deliveryLocation: null,
        deliveryPrice: 0,
        deliveryPriceEdited: false,
        enableHandlerKeys: true,
        referenceSelected: null,
        orderNeedPartialPaid: false,
        isOpenResultModal: false,
        isOpenSellersModal: false,
        isOpenDeliveryAtModal: false,
        isOpenObservationModal: false,
        isOpenMoneyBackModal: false,
        isOpenCustomersModal: false,
        isOpenValuePaidModal: false,
        isOpenReferencesModal: false,
        notVerifyAndDecreaseInventory: false,
        sourcePriceMerchant: false,
        deliveryAt: null,
        $minDeliveryDate: DateTime.now().toFormat("yyyy-MM-dd"),
        paymentMethod: "cash",
        portalPaymentMethods: [],
        anonymousReferences: [],
        lastProductSelected: { name: "" },
        productTmp: {
            id: 68,
            sku: "ACUNU02",
            slug: "product",
            name: "Product Name",
            references: []
        },
        orderResult: {
            id: 0,
            uuid: "",
            total: 20,
            createdAt: "2022-07-15T02:39:03.833Z",
            paymentStatus: "pending",
            paymentMethod: "cash",
            deliveryStatus: "pending",
            deliveryAt: "2022-07-15T02:39:03.833Z",
            valuePaid: 0,
            moneyIngress: 0,
            anonymousReferences: [],
            references: [],
            customer: null
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("commons", ["selectedMerchant", "isEnabledServiceSearch"]),
        hasMerchant() {
            return (
                this.$ability.hasPermission("order:CreateInMerchant") &&
                !!this.selectedMerchant.merchantId &&
                (this.sourcePriceMerchant || this.attachToSelectedMerchant)
            );
        },
        isValidMoneyIngress() {
            return this.moneyIngress >= this.orderResult.moneyIngress;
        },
        helpIntro() {
            if (this.sourcePriceMerchant) return "";
            return `Aquí registras ventas usando los precios de tu catálogo público — los mismos que ve un cliente en tu ${this.$siteType}. Por defecto no queda asociada a ninguna tienda ni descuenta inventario — activa "Asociar a [tienda]" en Opciones si quieres que sí lo haga. Si necesitas los precios propios de tu tienda, usa la sección "Venta en tienda".`;
        },
        helpSteps() {
            if (this.sourcePriceMerchant) {
                return [
                    `Busca los productos que deseas agregar: usa el buscador de la parte inferior, escanea el código de barras o cambia a modo "servicios" para buscar el servicio que necesitas.`,
                    "Selecciona el medio de pago con el que se realizará la transacción.",
                    `Verifica que el total sea correcto y haz clic en "Crear venta" (el botón aparece solo cuando hay productos seleccionados).`,
                    "Ajusta el precio de venta de cada producto en la lista si cobras un valor distinto al del catálogo. El total se calcula como: SUMA(precio de venta × cantidad), más el envío si es a domicilio.",
                    "Si la orden debe quedar en un estado de progreso, habilita el pago parcial."
                ];
            }
            return [
                `Busca los productos: usa el buscador de la parte inferior, escanea el código de barras o ingresa el monto del servicio. La búsqueda incluye todos los productos del ${this.$siteType}; si un producto no está en tu inventario, el sistema te indicará que debes registrarlo primero.`,
                "Selecciona el medio de pago con el que se realizará la transacción.",
                `Verifica que el total sea correcto y haz clic en "Crear venta" (el botón aparece solo cuando hay productos seleccionados).`,
                "Ajusta el precio de venta de cada producto en la lista si cobras un valor distinto al del catálogo. El total se calcula como: SUMA(precio de venta × cantidad), más el envío si es a domicilio.",
                'Para un pedido a domicilio marca "Pedido a domicilio" en Opciones: eliges el cliente, su dirección y el costo de envío.'
            ];
        },
        hasSalePriceErrors() {
            return this.references.some(
                (item) =>
                    !!validateLineSalePrice({
                        salePrice: resolveLineSalePrice(item),
                        priceOffer: item.priceOffer,
                        allowAboveCatalog: true
                    })
            );
        },
        isCreateDisabled() {
            return (
                !this.total ||
                this.hasSalePriceErrors ||
                (this.orderNeedHomeDelivery && !this.deliveryLocation)
            );
        },
        isPartialPayment() {
            return this.orderResult.moneyIngress < this.orderResult.total;
        },
        bottonActionLabel() {
            if (this.isQuote) {
                return "Cotización";
            }
            if (this.orderNeedPartialPaid || this.orderNeedDeliveryAt) {
                return "Orden";
            }

            return "Venta";
        }
    },
    watch: {
        orderNeedSeller(value) {
            if (value) {
                this.disableScanner();
                this.isOpenSellersModal = true;
            }
        },
        orderNeedCustomer(value) {
            if (value) {
                this.disableScanner();
                this.isOpenCustomersModal = true;
            } else if (this.orderNeedHomeDelivery) {
                this.orderNeedHomeDelivery = false;
            }
        },
        // pedido-domicilio-portal (design.md D5): domicilio exige cliente;
        // sin cliente se abre primero el buscador.
        orderNeedHomeDelivery(value) {
            if (!value) {
                this.clearHomeDelivery();
                return;
            }
            if (!this.customerId || !this.orderNeedCustomer) {
                this.orderNeedCustomer = true;
                return;
            }
            this.openHomeDeliveryModal();
        },
        references: {
            deep: true,
            handler() {
                this.refreshSuggestedDeliveryPrice();
            }
        },
        anonymousReferences: {
            deep: true,
            handler() {
                this.refreshSuggestedDeliveryPrice();
            }
        },
        orderNeedPartialPaid(value) {
            if (value) {
                this.disableScanner();
                this.isOpenValuePaidModal = true;
            }
        },
        orderNeedObservation(value) {
            if (value) {
                this.disableScanner();
                this.isOpenObservationModal = true;
            }
        },
        orderNeedDeliveryAt(value) {
            if (value) {
                this.disableScanner();
                this.isOpenDeliveryAtModal = true;
            }
        }
    },
    created() {
        this.$watch(
            () => this.$route,
            (newVal, oldVal) => {
                if (newVal !== oldVal) {
                    location.reload();
                }
            },
            {
                deep: true
            }
        );
        this.isOnboarding = this.$route.path.includes("onboarding");
        this.isQuote = this.$route.query.isQuote === "true";
        if (this.isQuote) {
            this.enableConfig = true;
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        this.sourcePriceMerchant = this.$route.name !== "OrderNew";

        if (
            !(await verifyPagePermission(
                this.sourcePriceMerchant
                    ? "order:CreateInMerchant"
                    : "order:Create",
                "/app/orders",
                this.sourcePriceMerchant
            ))
        )
            return;
        this.enabledScanner = !this.isEnabledServiceSearch;
        if (this.enabledScanner) this.addHandlerKey();
        await this.loadPortalPaymentMethods();
    },
    beforeDestroy() {
        this.removeHandlerKey();
        this.enabledScanner = false;
    },
    methods: {
        ...mapActions("orders", ["createOrder", "suggestOrderDeliveryPrice"]),
        ...mapActions("tenants", ["getTenantPaymentMethodsSettings"]),
        ...mapMutations("commons", ["TOGGLE_IS_ENABLED_SERVICE_SEARCH"]),
        async loadPortalPaymentMethods() {
            try {
                const data = await this.getTenantPaymentMethodsSettings();
                if (Array.isArray(data.portalPaymentMethodsAvailable)) {
                    this.portalPaymentMethods =
                        data.portalPaymentMethodsAvailable;
                }
            } catch {
                // fallback: show all payment methods
            }
        },
        async createOrderComponent() {
            if (!this.anonymousReferences.length && !this.references.length) {
                return;
            }
            this.$loading.show();
            try {
                const order = await this.createOrder({
                    paymentMethod: this.paymentMethod,
                    ...(this.hasMerchant
                        ? {
                              merchantId: this.selectedMerchant.merchantId,
                              merchantType: this.selectedMerchant.merchantType,
                              sourcePriceMerchant: this.sourcePriceMerchant
                          }
                        : null),
                    isQuote: this.isQuote,
                    verifyAndDecreaseInventory:
                        !this.notVerifyAndDecreaseInventory,
                    ...{
                        ...(this.orderNeedSeller
                            ? { sellerId: this.sellerId }
                            : null)
                    },
                    ...{
                        ...(this.orderNeedCustomer
                            ? { customerId: this.customerId }
                            : null)
                    },
                    ...{
                        ...(this.orderNeedObservation
                            ? { observation: this.observation }
                            : null)
                    },
                    ...{
                        ...(this.orderNeedPartialPaid
                            ? { valuePaid: this.valuePaid }
                            : null)
                    },
                    ...{
                        ...(this.orderNeedDeliveryAt && this.deliveryAt
                            ? {
                                  deliveryAt: DateTime.fromJSDate(
                                      new Date(this.deliveryAt)
                                  )
                                      .toUTC()
                                      .toFormat("yyyy-MM-dd")
                              }
                            : null)
                    },
                    ...(this.orderNeedHomeDelivery && this.deliveryLocation
                        ? {
                              deliveryOption: "home",
                              deliveryPrice: this.deliveryPrice,
                              ...(this.deliveryLocation.customerLocationId
                                  ? {
                                        customerLocationId:
                                            this.deliveryLocation
                                                .customerLocationId
                                    }
                                  : {
                                        location: this.deliveryLocation.location
                                    })
                          }
                        : null),
                    references: this.references.map((item) => ({
                        id: item.id,
                        quantity: item.quantity,
                        salePrice: resolveLineSalePrice(item)
                    })),
                    anonymousReferences: this.anonymousReferences.map(
                        (item) => ({
                            priceOffer: item.priceOffer,
                            quantity: item.quantity,
                            appliedDiscountPerReference:
                                item.appliedDiscountPerReference,
                            product: item.product,
                            size: item.size
                        })
                    )
                });

                this.orderResult = {
                    id: order.id,
                    uuid: order.uuid,
                    customerName: this.customerName,
                    total: order.total,
                    valuePaid: order.valuePaid,
                    moneyIngress: order.moneyIngress,
                    customer: order.customer,
                    createdAt: order.createdAt,
                    paymentStatus: order.paymentStatus,
                    paymentMethod: order.paymentMethod,
                    deliveryAt: order.deliveryAt,
                    references: [...this.references],
                    anonymousReferences: [...this.anonymousReferences]
                };
                this.$analytics.trackEvent(
                    this.isQuote ? "quote_created" : "order_created",
                    {
                        payment_method: order.paymentMethod,
                        payment_status: order.paymentStatus,
                        total: order.total,
                        items_count:
                            this.references.length +
                            this.anonymousReferences.length,
                        source_price_merchant: this.sourcePriceMerchant,
                        has_customer: !!this.customerId,
                        has_seller: !!this.sellerId,
                        is_partial_payment: this.orderNeedPartialPaid
                    }
                );
                this.$loading.hide();
                this.removeHandlerKey();
                this.moneyIngress = order.moneyIngress;
                if (this.paymentMethod === "cash" && this.moneyIngress > 0) {
                    this.$sound.play("cash", 0.4);
                    this.isOpenMoneyBackModal = true;
                    return;
                }
                this.isOpenResultModal = true;

                setTimeout(() => {
                    this.isOpenResultModal = false;
                }, 60000);
            } catch (error) {
                this.$loading.hide();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async selectProduct(product) {
            this.enabledScanner = false;
            this.productTmp = { ...product };
            if (product.references.length === 0) {
                await this.$swal.fire({
                    icon: "error",
                    title: "Producto invalido",
                    text: "Entra a los detalles del producto y verifica que tenga referencias"
                });
                window
                    .open(`/app/products/detail/${product.id}`, "_blank")
                    .focus();
            } else if (product.references.length === 1) {
                this.validatePush(product.references[0], product);
                this.isOpenReferencesModal = false;
                this.referenceSelected = -1;
                this.enabledScanner = true;
            } else {
                this.productTmp = { ...product };
                this.referenceSelected = -1;
                this.isOpenReferencesModal = true;
            }
        },
        selectDeliveryAt() {
            this.isOpenDeliveryAtModal = false;
        },
        selectObservation() {
            this.isOpenObservationModal = false;
        },
        selectValuePaid() {
            this.isOpenValuePaidModal = false;
        },
        calculateTotals() {
            const productsTotal = [
                ...this.references,
                ...this.anonymousReferences
            ].reduce((a, b) => a + resolveLineSalePrice(b) * b.quantity, 0);
            const deliveryPrice =
                this.orderNeedHomeDelivery && this.deliveryLocation
                    ? Number(this.deliveryPrice) || 0
                    : 0;
            const total = productsTotal + deliveryPrice;
            if (!this.orderNeedPartialPaid) {
                this.valuePaid = +total;
            }
            this.total = +total;
            this.subtotal = productsTotal / (1 + 0.19);
        },
        openHomeDeliveryModal() {
            this.disableScanner();
            this.isOpenHomeDeliveryModal = true;
        },
        closeHomeDeliveryModal() {
            this.orderNeedHomeDelivery = !!this.deliveryLocation;
            this.enableScanner();
        },
        clearHomeDelivery() {
            this.isOpenHomeDeliveryModal = false;
            this.deliveryLocation = null;
            this.deliveryPrice = 0;
            this.deliveryPriceEdited = false;
            this.calculateTotals();
        },
        onDeliveryLocationChange(value) {
            const cityChanged =
                !this.deliveryLocation ||
                !value ||
                this.deliveryLocation.cityCode !== value.cityCode;
            this.deliveryLocation = value;
            if (cityChanged) {
                this.deliveryPriceEdited = false;
                this.refreshSuggestedDeliveryPrice();
            }
            this.calculateTotals();
        },
        onDeliveryPriceInput() {
            this.deliveryPriceEdited = true;
            this.calculateTotals();
        },
        // pedido-domicilio-portal (design.md D4): el sugerido no pisa un
        // envío escrito a mano y, si falla, el campo sigue editable.
        async refreshSuggestedDeliveryPrice() {
            if (
                !this.orderNeedHomeDelivery ||
                !this.deliveryLocation ||
                this.deliveryPriceEdited
            ) {
                return;
            }
            try {
                const { deliveryPrice } = await this.suggestOrderDeliveryPrice({
                    cityCode: this.deliveryLocation.cityCode,
                    references: this.references.map((item) => ({
                        id: item.id,
                        quantity: item.quantity,
                        salePrice: resolveLineSalePrice(item)
                    })),
                    anonymousReferences: this.anonymousReferences.map(
                        (item) => ({
                            priceOffer: item.priceOffer,
                            quantity: item.quantity
                        })
                    )
                });
                if (!this.deliveryPriceEdited) {
                    this.deliveryPrice = deliveryPrice;
                    this.calculateTotals();
                }
            } catch {
                // sin sugerido: el costo de envío se escribe a mano
            }
        },
        validatePush(reference, product) {
            const index = this.references.findIndex(
                (item) => item.id === reference.id
            );

            if (index === -1) {
                this.references.unshift({
                    ...reference,
                    quantity: 1,
                    appliedDiscountPerReference: 0,
                    productId: product.id,
                    product: {
                        id: product.id,
                        slug: product.slug,
                        name: product.name,
                        sku: product.sku
                    }
                });
            } else {
                let references = [...this.references];
                references[index] = {
                    ...references[index],
                    quantity: references[index].quantity + 1
                };
                this.references = [...references];
            }
            this.calculateTotals();
        },
        pushReference() {
            if (this.referenceSelected < 0) return false;
            const reference =
                this.productTmp.references[this.referenceSelected];
            this.validatePush(reference, this.productTmp);
            this.isOpenReferencesModal = false;
            this.referenceSelected = -1;
            this.enabledScanner = true;
        },
        selectReference(index) {
            if (index < 0) {
                return;
            }
            this.enabledScanner = false;
            this.referenceSelected = index;
            this.pushReference();
        },
        incrementReferenceSelect() {
            this.enabledScanner = false;
            if (!this.isOpenReferencesModal) return;
            if (this.referenceSelected < 0) {
                this.referenceSelected = 0;
            } else if (
                this.referenceSelected <
                this.productTmp.references.length - 1
            ) {
                this.referenceSelected = this.referenceSelected + 1;
            }
        },
        decrementReferenceSelect() {
            this.enabledScanner = false;
            if (!this.isOpenReferencesModal) return;
            if (this.referenceSelected < 0) {
                this.referenceSelected = 0;
            } else if (this.referenceSelected > 0) {
                this.referenceSelected = this.referenceSelected - 1;
            }
        },
        handleKey(event) {
            const actions = {
                ArrowDown: this.onDown,
                ArrowUp: this.onUp,
                Escape: this.onEscape,
                Enter: this.onEnter
            };
            const fun = actions[event.key] || function () {};
            fun();
        },
        onEscape() {
            this.closeReferenceModal();
        },
        onEnter() {
            this.enabledScanner = false;
            if (this.isOpenReferencesModal) {
                return this.pushReference();
            }
            if (
                !this.isOpenCustomersModal &&
                !this.isOpenReferencesModal &&
                !this.isOpenSellersModal
            ) {
                this.createOrderComponent();
            }
        },
        onUp() {
            if (this.isOpenReferencesModal) {
                return this.decrementReferenceSelect();
            }
            if (
                !this.isOpenCustomersModal &&
                !this.isOpenReferencesModal &&
                !this.isOpenSellersModal
            ) {
                document
                    .getElementById("app-container")
                    .scrollIntoView({ behavior: "smooth" });
            }
        },
        onDown() {
            if (this.isOpenReferencesModal) {
                return this.incrementReferenceSelect();
            }
            if (
                !this.isOpenCustomersModal &&
                !this.isOpenReferencesModal &&
                !this.isOpenSellersModal
            ) {
                document.getElementById("app-container").scrollIntoView(false);
            }
        },
        changeQuantityReference({
            appliedDiscountPerReference,
            id,
            quantity,
            salePrice
        }) {
            const index = this.references.findIndex((item) => item.id == id);
            let references = [...this.references];
            references[index] = {
                ...references[index],
                quantity,
                appliedDiscountPerReference,
                ...(salePrice !== undefined ? { salePrice } : {})
            };
            this.references = [...references];
            this.calculateTotals();
        },
        changeQuantityAnonymousReference({
            anonymousId,
            appliedDiscountPerReference,
            priceOffer,
            product,
            quantity,
            size = ""
        }) {
            const index = this.anonymousReferences.findIndex(
                (item) => item.anonymousId === anonymousId
            );
            const anonymousReferences = [...this.anonymousReferences];
            anonymousReferences[index] = {
                ...anonymousReferences[index],
                quantity,
                appliedDiscountPerReference,
                product,
                priceOffer,
                size
            };
            this.anonymousReferences = [...anonymousReferences];
            this.calculateTotals();
        },
        removeReference({ id }) {
            const index = this.references.findIndex((item) => item.id == id);
            let references = [...this.references];
            references.splice(index, 1);
            this.references = [...references];
            this.calculateTotals();
        },
        removeAnonymousReference({ anonymousId }) {
            const index = this.anonymousReferences.findIndex(
                (item) => item.anonymousId === anonymousId
            );
            const anonymousReferences = [...this.anonymousReferences];
            anonymousReferences.splice(index, 1);
            this.anonymousReferences = [...anonymousReferences];
            this.calculateTotals();
        },
        selectProductAnonymous({
            anonymousId,
            appliedDiscountPerReference,
            priceOffer,
            product,
            quantity,
            size
        }) {
            const index = this.anonymousReferences.findIndex(
                (item) => anonymousId === item.anonymousId
            );
            if (index === -1) {
                this.anonymousReferences.push({
                    anonymousId: this.anonymousReferences.length,
                    priceOffer,
                    quantity,
                    product,
                    size,
                    appliedDiscountPerReference:
                        appliedDiscountPerReference || 0
                });
            } else {
                const anonymousReferences = [...this.anonymousReferences];
                anonymousReferences[index] = {
                    ...anonymousReferences[index],
                    quantity: anonymousReferences[index].quantity + quantity
                };
                this.anonymousReferences = [...anonymousReferences];
            }
            this.calculateTotals();
        },
        selectSeller(seller) {
            this.sellerId = seller.id;
            this.sellerName = `${seller.firstName} ${seller.lastName}`;
            this.isOpenSellersModal = false;
        },
        selectCustomer(customer) {
            if (this.customerId !== customer.id) {
                this.deliveryLocation = null;
            }
            this.customerId = customer.id;
            this.customerName = `${customer.firstName} ${customer.lastName}`;
            this.isOpenCustomersModal = false;
            if (this.orderNeedHomeDelivery && !this.deliveryLocation) {
                this.$nextTick(() => this.openHomeDeliveryModal());
            }
        },
        closeReferenceModal() {
            this.isOpenReferencesModal = false;
            this.enabledScanner = true;
        },
        closeSellersModal() {
            this.orderNeedSeller = !!this.sellerId;
            this.enableScanner();
        },
        closeCustomersModal() {
            this.orderNeedCustomer = !!this.customerId;
            if (!this.customerId) {
                this.orderNeedHomeDelivery = false;
            }
            this.enableScanner();
        },
        closeDeliveryAtModal() {
            this.orderNeedDeliveryAt = !!this.deliveryAt;
            this.enableScanner();
        },
        closeResult() {
            this.paymentMethod = "cash";
            this.total = 0;
            this.subtotal = 0;
            this.enabledScanner = true;
            this.anonymousReferences = [];
            this.references = [];
            this.orderNeedSeller = false;
            this.orderNeedCustomer = false;
            this.orderNeedObservation = false;
            this.observation = "";
            this.orderNeedPartialPaid = false;
            this.valuePaid = 0;
            this.isQuote = false;
            this.deliveryAt = null;
            this.orderNeedHomeDelivery = false;
            this.deliveryLocation = null;
            this.deliveryPrice = 0;
            this.deliveryPriceEdited = false;
            if (this.isOnboarding) {
                return this.$router.push("/onboarding/marketplace/finish");
            }
        },
        removeHandlerKey() {
            document.removeEventListener("keydown", this.handleKey);
        },
        addHandlerKey() {
            document.addEventListener("keydown", this.handleKey);
        },
        enableScanner() {
            this.enabledScanner = true;
            this.addHandlerKey();
        },
        disableScanner() {
            this.enabledScanner = false;
            this.removeHandlerKey();
        },
        toggleIsEnabledServices() {
            if (this.isEnabledServiceSearch) {
                this.enableScanner();
            } else {
                this.disableScanner();
            }
            this.TOGGLE_IS_ENABLED_SERVICE_SEARCH();
        },
        onEnterMoneyIngress() {
            this.isOpenResultModal = true;
            this.moneyBack = this.moneyIngress - this.orderResult.moneyIngress;
        }
    }
};
</script>
